import Icon from "components/Icons/Icon";
import { useAppDownloadUrl } from "components/common/useAppStoreLink";
import trackEvent from "components/tracking/trackEvent";
import trackFathom from "components/tracking/trackFathom";
import { PlanGateModal, usePlanGate } from "components/website/components/planGate";
import React, { useState } from "react";
import { OsTypes, isMobile, osName } from "react-device-detect";
import useReactRouter from "use-react-router";
import { t } from "i18n/index";
import { localizeHref, MarketingLangCode } from "i18n/urlLocale";
import FAQSection from "../components/FAQSection";
import DownloadAppModal from "../components/DownloadAppModal";
import Footer from "../components/Footer";
import Navigation from "../components/navigation/Navigation";
import LanguageSwitcher from "../components/LanguageSwitcher";
import "../css/ProPage.scss";
import "../css/Website.scss";
import AiVisual1 from "../img/screenshots/ai/AI1.png";
import { PRO_CONTENT } from "../marketing/content/featurePageContent";
import { getPricingTranslations } from "../marketing/i18n/pages/pricing";

const S_PATH =
	"M17.3912 99.1864C14.9217 99.1864 12.9613 101.159 13.0092 103.593C13.0589 106.025 15.0991 108 17.5668 108C20.0363 108 21.9967 106.025 21.9488 103.593C21.8991 101.159 19.8589 99.1864 17.3912 99.1864ZM65.3336 33.3614C76.1235 30.4927 80.4221 24.1043 80.431 16.7419C80.4434 6.75566 72.3519 0 59.0871 0C38.1939 0 0 22.4846 0 43.1187C0 59.3036 21.0387 64.5548 35.3004 67.8972C49.5639 71.2377 60.588 74.2732 60.588 78.2187C60.588 82.1625 48.0081 91.0523 27.7464 97.4815C25.575 98.2036 24.3065 100.144 24.9097 101.816C25.5147 103.487 27.7145 104.079 29.9356 103.533C38.3749 101.452 78.201 92.3474 78.201 74.4204C78.201 56.4935 35.2862 49.1612 35.2862 45.9005C35.2862 42.5528 56.6017 35.6819 65.3336 33.3614Z";

const UNLOCK_CARDS = [
	{
		iconName: "magic",
		iconVariant: "green",
		titleKey: "pricing.unlocks.card1.title",
		title: "Unlimited AI trip generation",
		descKey: "pricing.unlocks.card1.desc",
		desc: "Generate a full day-by-day itinerary for any destination in under 2 minutes: as many trips as you want, with no limits.",
	},
	{
		iconName: "calendar",
		iconVariant: "green",
		titleKey: "pricing.unlocks.card2.title",
		title: "Unlimited AI day-plan generation",
		descKey: "pricing.unlocks.card2.desc",
		desc: "Already have a trip? Generate a detailed day plan for any single day with one tap: activities, restaurants and transport.",
	},
	{
		iconName: "document",
		iconVariant: "green",
		titleKey: "pricing.unlocks.card3.title",
		title: "PDF export",
		descKey: "pricing.unlocks.card3.desc",
		desc: "Export your complete itinerary as a PDF. Share it with travel companions, keep an offline copy, or send it to a travel agent.",
	},
	{
		iconName: "mail",
		iconVariant: "green",
		titleKey: "pricing.unlocks.card4.title",
		title: "Forward email reservations",
		descKey: "pricing.unlocks.card4.desc",
		desc: "Forward any booking confirmation email directly to your trip. Flights, hotels, restaurants, all imported automatically.",
	},
	{
		iconName: "calendar",
		iconVariant: "green",
		titleKey: "pricing.unlocks.card5.title",
		title: "Calendar sync",
		descKey: "pricing.unlocks.card5.desc",
		desc: "Sync your Stippl itinerary with your calendar. Every activity and destination lands in your phone's calendar automatically.",
	},
	{
		iconName: "budget",
		iconVariant: "purple",
		titleKey: "pricing.unlocks.card6.title",
		title: "Expense export & custom categories",
		descKey: "pricing.unlocks.card6.desc",
		desc: "Export your full travel budget as a CSV. Create custom expense categories that match how you actually spend on trips.",
	},
	{
		iconName: "movie",
		iconVariant: "green",
		titleKey: "pricing.unlocks.card7.title",
		title: "Customise your travel reel",
		descKey: "pricing.unlocks.card7.desc",
		desc: "Include up to 10 photos per destination in your travel reel (3 on free). Customise the style, music and layout of your trip movie.",
	},
	{
		iconName: "ledger",
		iconVariant: "pink",
		titleKey: "pricing.unlocks.card8.title",
		title: "Re-use your packing checklists",
		descKey: "pricing.unlocks.card8.desc",
		desc: "Save and reuse your own packing lists across trips. Build your perfect checklist once, apply it to every trip after that.",
	},
	{
		iconName: "map",
		iconVariant: "teal",
		titleKey: "pricing.unlocks.card9.title",
		title: "Photobook discount",
		descKey: "pricing.unlocks.card9.desc",
		desc: "Get 20% off when you turn your travel journal into a printed travel photo book.",
	},
];

type CellVal = boolean | string;

interface CompFeature {
	nameKey: string;
	name: string;
	free: CellVal;
	freeKey?: string;
	pro: CellVal;
}

interface CompSection {
	titleKey: string;
	title: string;
	iconName?: string;
	iconFill?: string;
	iconStroke?: string;
	iconPathProps?: Record<string, string>;
	features: CompFeature[];
}

const COMPARISON_DATA: CompSection[] = [
	{
		titleKey: "pricing.compare.section.general",
		title: "General",
		iconName: "kanban",
		iconPathProps: { stroke: "#0f3e4a" },
		features: [
			{ nameKey: "pricing.compare.general.unlimitedTrips", name: "Unlimited trips", free: true, pro: true },
			{ nameKey: "pricing.compare.general.cloudSync", name: "Cloud sync", free: true, pro: true },
			{ nameKey: "pricing.compare.general.languages", name: "Available in 7 languages", free: true, pro: true },
			{ nameKey: "pricing.compare.general.curatedTrips", name: "Free curated trips", free: true, pro: true },
			{ nameKey: "pricing.compare.general.timezoneChanges", name: "Timezone changes", free: true, pro: true },
			{ nameKey: "pricing.compare.general.mapStyles", name: "Personalised map styles and colours", free: false, pro: true },
		],
	},
	{
		titleKey: "pricing.compare.section.planner",
		title: "Planner",
		iconName: "marker",
		iconFill: "#0f3e4a",
		features: [
			{ nameKey: "pricing.compare.planner.destinations", name: "Unlimited destinations", free: true, pro: true },
			{ nameKey: "pricing.compare.planner.activities", name: "Unlimited activities", free: true, pro: true },
			{ nameKey: "pricing.compare.planner.coplanners", name: "Unlimited co-planners", free: true, pro: true },
			{ nameKey: "pricing.compare.planner.sleepOptions", name: "Unlimited sleep options", free: true, pro: true },
			{ nameKey: "pricing.compare.planner.transportOption", name: "Add any transport option", free: true, pro: true },
			{ nameKey: "pricing.compare.planner.aiTripGen", name: "Unlimited AI trip generations", free: false, pro: true },
			{ nameKey: "pricing.compare.planner.aiDayPlanGen", name: "Unlimited AI day plan generations", free: false, pro: true },
			{ nameKey: "pricing.compare.planner.aiRouteOpt", name: "AI route optimisation", free: false, pro: true },
			{ nameKey: "pricing.compare.planner.forwardEmail", name: "Forward email reservations", free: false, pro: true },
			{ nameKey: "pricing.compare.planner.calendarSync", name: "Calendar sync", free: false, pro: true },
			{ nameKey: "pricing.compare.planner.pdfExport", name: "PDF export", free: false, pro: true },
		],
	},
	{
		titleKey: "pricing.compare.section.budget",
		title: "Budget",
		iconName: "money",
		iconFill: "#0f3e4a",
		features: [
			{ nameKey: "pricing.compare.budget.unlimitedExpenses", name: "Unlimited expenses", free: true, pro: true },
			{ nameKey: "pricing.compare.budget.viewSettle", name: "View & settle expenses", free: true, pro: true },
			{ nameKey: "pricing.compare.budget.currencyConversion", name: "Currency conversion", free: true, pro: true },
			{ nameKey: "pricing.compare.budget.connectExpenses", name: "Connect expenses to trip", free: true, pro: true },
			{ nameKey: "pricing.compare.budget.smartBalance", name: "Smart balance calculation", free: true, pro: true },
			{ nameKey: "pricing.compare.budget.expenseExport", name: "Expense export", free: false, pro: true },
			{ nameKey: "pricing.compare.budget.customCategories", name: "Custom categories", free: false, pro: true },
		],
	},
	{
		titleKey: "pricing.compare.section.checklist",
		title: "Checklist",
		iconName: "clothing",
		iconFill: "#0f3e4a",
		features: [
			{ nameKey: "pricing.compare.checklist.unlimitedChecklists", name: "Create unlimited checklists", free: true, pro: true },
			{ nameKey: "pricing.compare.checklist.predefinedLists", name: "24 predefined packing lists", free: true, pro: true },
			{ nameKey: "pricing.compare.checklist.sharedLists", name: "Shared packing lists", free: true, pro: true },
			{ nameKey: "pricing.compare.checklist.reuseChecklists", name: "Re-use your own checklists", free: false, pro: true },
		],
	},
	{
		titleKey: "pricing.compare.section.journal",
		title: "Travel journal",
		iconName: "edit-pencil",
		iconStroke: "#0f3e4a",
		features: [
			{ nameKey: "pricing.compare.journal.unlimitedEntries", name: "Unlimited journal entries", free: true, pro: true },
			{ nameKey: "pricing.compare.journal.picturesPerPost", name: "Up to 50 pictures per post", free: true, pro: true },
			{ nameKey: "pricing.compare.journal.multipleAuthors", name: "Multiple journal authors", free: true, pro: true },
			{ nameKey: "pricing.compare.journal.reminders", name: "Automated journal reminders", free: true, pro: true },
			{ nameKey: "pricing.compare.journal.photobookDiscount", name: "20% discount on photobooks", free: false, pro: true },
		],
	},
	{
		titleKey: "pricing.compare.section.reel",
		title: "Travel reel",
		iconName: "playTriangle",
		iconFill: "#0f3e4a",
		features: [
			{ nameKey: "pricing.compare.reel.movieRender", name: "Movie render for each trip", free: true, pro: true },
			{ nameKey: "pricing.compare.reel.reRenders", name: "Unlimited re-renders", free: true, pro: true },
			{ nameKey: "pricing.compare.reel.autoTransport", name: "Automated transport type", free: true, pro: true },
			{
				nameKey: "pricing.compare.reel.picturesPerDest",
				name: "Up to 10 pictures per destination (3 on free)",
				free: "3 photos",
				freeKey: "pricing.compare.reel.freeCellPhotos",
				pro: true,
			},
			{ nameKey: "pricing.compare.reel.customiseReel", name: "Customise travel reel", free: false, pro: true },
		],
	},
];

const PRICING_FEATURES = [
	{
		labelKey: "pricing.card.feat.aiGeneration",
		label: "Unlimited AI trip & day-plan generation",
		catKey: "pricing.compare.section.planner",
		cat: "Planner",
	},
	{ labelKey: "pricing.compare.planner.pdfExport", label: "PDF export", catKey: "pricing.compare.section.planner", cat: "Planner" },
	{
		labelKey: "pricing.compare.planner.forwardEmail",
		label: "Forward email reservations",
		catKey: "pricing.compare.section.planner",
		cat: "Planner",
	},
	{ labelKey: "pricing.compare.planner.calendarSync", label: "Calendar sync", catKey: "pricing.compare.section.planner", cat: "Planner" },
	{
		labelKey: "pricing.card.feat.expenseCustom",
		label: "Expense export & custom categories",
		catKey: "pricing.compare.section.budget",
		cat: "Budget",
	},
	{
		labelKey: "pricing.card.feat.reuseChecklists",
		label: "Re-use your own packing checklists",
		catKey: "pricing.compare.section.checklist",
		cat: "Checklist",
	},
	{
		labelKey: "pricing.card.feat.customiseReel",
		label: "Customise travel reel · 10 photos per destination",
		catKey: "pricing.compare.section.reel",
		cat: "Travel reel",
	},
	{
		labelKey: "pricing.compare.journal.photobookDiscount",
		label: "20% discount on photobooks",
		catKey: "pricing.compare.section.journal",
		cat: "Travel journal",
	},
	{
		labelKey: "pricing.compare.general.mapStyles",
		label: "Personalised map styles and colours",
		catKey: "pricing.compare.section.general",
		cat: "General",
	},
];

function unlockIconFill(variant: string): string {
	if (variant === "purple") return "var(--main4)";
	if (variant === "pink") return "var(--main2)";
	return "var(--color-primary)";
}

function CellValue({ val }: { val: CellVal }) {
	if (val === true) return <span className="check-green">✓</span>;
	if (val === false) return <span className="table-dash">—</span>;
	return <span className="table-dash">{val}</span>;
}

interface B2CPricingPageProps {
	lang?: string;
	uiTranslations?: Record<string, string>;
}

const B2CPricingPage = ({ lang }: B2CPricingPageProps) => {
	const { match } = useReactRouter();
	const translations = getPricingTranslations(lang || "en");

	const translateCell = (val: CellVal, key?: string): CellVal => (typeof val === "string" && key ? t(translations, key, val) : val);
	const [showDownloadModal, setShowDownloadModal] = useState(false);
	// 123yb691qz1: every /register anchor on this page opens the signup modal in place instead
	// of navigating; `mobile: "store"` keeps 123yb691bdn's rule that a phone goes to the store
	// instead. `match.path` reuses the label `handleTrack("signup")` already tracked under.
	const gate = usePlanGate(match.path, "store");

	const handleTrack = (type: "signup" | "download" | "appstore" | "googleplay") => {
		if (type === "appstore") {
			trackFathom("FD21GVOP");
			trackEvent("click_appstore_cta", { path: match.path }, "marketing_site");
		} else if (type === "googleplay") {
			trackFathom("BNQKH0MH");
			trackEvent("click_googleplay_cta", { path: match.path }, "marketing_site");
		} else if (osName === OsTypes.Android && type === "download") {
			trackFathom("BNQKH0MH");
			trackEvent("click_googleplay_cta", { path: match.path }, "marketing_site");
		} else if (osName === OsTypes.IOS && type === "download") {
			trackFathom("FD21GVOP");
			trackEvent("click_appstore_cta", { path: match.path }, "marketing_site");
		} else {
			trackEvent("click_signup_cta", { path: match.path }, "marketing_site");
			trackFathom("E4NT4HWN");
		}
	};

	// 123yb6937h3: the "Download the app" CTA below resolves its href through this -- always a real,
	// attributed OneLink, including on the very first render before mount (see useAppDownloadUrl's
	// doc comment in useAppStoreLink.ts). Previously this was a <button
	// onClick={setShowDownloadModal(true)}> with no href at all, so the server HTML had a dead
	// control -- confirmed on production by QA's curl retest of 123yb6937h3.
	const appDownloadUrl = useAppDownloadUrl();

	return (
		<div className="pro-page" id="stippl-pro">
			<Navigation impact={true} langMenu={<LanguageSwitcher type="page" />} />

			{/* HERO */}
			<section className="hero">
				<svg className="hero-s-mark" width="480" height="640" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d={S_PATH} fill="white" />
				</svg>
				<svg className="hero-s-mark-2" width="280" height="373" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d={S_PATH} fill="white" />
				</svg>
				<div className="hero-inner">
					<div>
						<div className="pro-hero-badge">Stippl Pro</div>
						<h1>
							{t(translations, "pricing.hero.titleLine1", "Travel better.")}
							<br />
							<span>{t(translations, "pricing.hero.titleLine2", "Plan smarter.")}</span>
						</h1>
						<p className="hero-sub">
							{t(
								translations,
								"pricing.hero.subtitle",
								"Trip planning, budget tracking, packing lists, travel journal: all free, forever. Pro adds the power features: AI-generated itineraries, PDF export, calendar sync and more."
							)}
						</p>
						<div className="hero-price">
							<span className="price-num">€4.99</span>
							<span className="price-period">/ {t(translations, "pricing.hero.pricePeriod", "month · cancel anytime")}</span>
						</div>
						<div className="hero-ctas">
							{/* 123yb6937h3: on a phone the anchor is simply followed -- OneLink does the OS split
								(App Store vs Play Store) server-side, which is the ticket's acceptance criterion.
								On desktop we still want DownloadAppModal's QR code as the affordance, so the click
								handler -- browser-only, unlike a render-time device check -- prevents the default
								navigation and opens the modal instead. */}
							<a
								href={appDownloadUrl}
								className="hero-glass-btn"
								onClick={(e) => {
									handleTrack("download");
									if (!isMobile) {
										e.preventDefault();
										setShowDownloadModal(true);
									}
								}}
							>
								<div className="hero-glass-btn__icon">
									<svg width="16" height="21" viewBox="0 0 81 108" fill="none">
										<path d={S_PATH} fill="#fff" />
									</svg>
								</div>
								<span className="hero-glass-btn__label">{t(translations, "pricing.hero.downloadApp", "Download the app")}</span>
							</a>
							<a
							href="/register"
							className="hero-web-link"
							onClick={(e) => {
								e.preventDefault();
								gate.requestPlan();
							}}
						>
								{t(translations, "pricing.hero.startWeb", "Start on web →")}
							</a>
						</div>
					</div>
					<div className="hero-visual">
						<div className="placeholder-visual">
							<img src={AiVisual1} alt={t(translations, "pricing.hero.visualAlt", "AI itinerary generator")} />
						</div>
					</div>
				</div>
			</section>

			{/* WHAT PRO UNLOCKS */}
			<section>
				<div className="wrap">
					<div className="text-center">
						<div className="chip chip-green">{t(translations, "pricing.unlocks.chip", "Pro features")}</div>
						<h2 className="section-h">{t(translations, "pricing.unlocks.heading", "Everything Pro unlocks")}</h2>
						<p className="section-sub">
							{t(translations, "pricing.unlocks.introPre", "These features are exclusive to Stippl Pro. Everything else stays free: the full ")}
							<a href={localizeHref("/itinerary-planner", lang as MarketingLangCode)}>{t(translations, "pricing.unlocks.linkPlanner", "trip planner")}</a>,{" "}
							<a href={localizeHref("/expense-tracker", lang as MarketingLangCode)}>{t(translations, "pricing.unlocks.linkBudget", "budget tracker")}</a>,{" "}
							<a href={localizeHref("/travel-journal", lang as MarketingLangCode)}>{t(translations, "pricing.unlocks.linkJournal", "journal")}</a>{" and "}
							<a href={localizeHref("/packing-list", lang as MarketingLangCode)}>{t(translations, "pricing.unlocks.linkPacking", "packing list")}</a>
							{t(translations, "pricing.unlocks.introPost", ".")}
						</p>
					</div>
					<div className="unlocks-grid">
						{UNLOCK_CARDS.map((card, i) => (
							<div key={i} className="unlock-card">
								<div className={`unlock-icon unlock-icon--${card.iconVariant}`}>
									<Icon name={card.iconName as any} size={22} fill={unlockIconFill(card.iconVariant)} />
								</div>
								<h3>{t(translations, card.titleKey, card.title)}</h3>
								<p>{t(translations, card.descKey, card.desc)}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* FREE vs PRO COMPARISON */}
			<section id="compare" style={{ background: "#f9fcfd" }}>
				<div className="wrap">
					<div className="text-center">
						<div className="chip chip-green">{t(translations, "pricing.compare.chip", "Free vs Pro")}</div>
						<h2 className="section-h">{t(translations, "pricing.compare.heading", "Everything included, clearly")}</h2>
						<p className="section-sub">
							{t(translations, "pricing.compare.subtitle", "Stippl gives you a lot for free. Here's exactly what each plan includes.")}
						</p>
					</div>
					<div className="comp-wrap">
						<table className="comp-table">
							<thead>
								<tr>
									<th className="feat-col">{t(translations, "pricing.compare.table.feature", "Feature")}</th>
									<th className="free-col">{t(translations, "pricing.compare.table.free", "Free")}</th>
									<th className="pro-col">{t(translations, "pricing.compare.table.pro", "Pro")}</th>
								</tr>
							</thead>
							<tbody>
								{COMPARISON_DATA.map((section) => (
									<React.Fragment key={section.title}>
										<tr className="comp-section-row">
											<td colSpan={3}>
												<span>
													{section.iconName && (
														<Icon
															name={section.iconName as any}
															size={16}
															{...(section.iconFill && { fill: section.iconFill })}
															{...(section.iconStroke && { stroke: section.iconStroke })}
															{...(section.iconPathProps && { pathProps: section.iconPathProps })}
														/>
													)}
													{t(translations, section.titleKey, section.title)}
												</span>
											</td>
										</tr>
										{section.features.map((feat) => (
											<tr key={feat.name}>
												<td className="feat-name">{t(translations, feat.nameKey, feat.name)}</td>
												<td>
													<CellValue val={translateCell(feat.free, feat.freeKey)} />
												</td>
												<td className="pro-cell">
													<CellValue val={feat.pro} />
												</td>
											</tr>
										))}
									</React.Fragment>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</section>

			{/* PRICING CARD */}
			<section className="pricing-section">
				<div className="wrap">
					<div className="text-center">
						<div className="chip chip-green">{t(translations, "pricing.card.chip", "Pricing")}</div>
						<h2 className="section-h">{t(translations, "pricing.card.heading", "Simple, honest pricing")}</h2>
						<p className="section-sub">{t(translations, "pricing.card.subtitle", "One plan. Everything included. Cancel any time.")}</p>
					</div>
					<div className="pricing-card-wrap">
						<div className="pricing-card">
							<div className="pricing-card-header">
								<svg className="pricing-card-s" width="200" height="267" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d={S_PATH} fill="white" />
								</svg>
								<div className="pricing-label">Stippl Pro</div>
								<div className="pricing-big">€4.99</div>
								<div className="pricing-period">{t(translations, "pricing.card.period", "per month")}</div>
								<div className="pricing-cancel">{t(translations, "pricing.card.cancelAnytime", "Cancel any time")}</div>
							</div>
							<div className="pricing-card-body">
								<div className="pricing-features">
									{PRICING_FEATURES.map((feat, i) => (
										<div key={i} className="pricing-feat">
											<div className="pricing-feat-check">✓</div>
											<div className="pricing-feat-label">
												<div>{t(translations, feat.labelKey, feat.label)}</div>
												<div className="pricing-feat-cat">{t(translations, feat.catKey, feat.cat)}</div>
											</div>
										</div>
									))}
								</div>
								<a
								href="/register"
								className="pricing-cta"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
									{t(translations, "pricing.card.cta", "Get Stippl Pro, €4.99/mo")}
								</a>
								<div className="pricing-note">{t(translations, "pricing.card.note", "Cancel any time · iOS, Android & web")}</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* FAQ */}
			<section className="feature-section">
				<div className="wrap">
					<FAQSection
						heading={t(translations, "pricing.faq.heading", "Common questions about Stippl Pro")}
						items={PRO_CONTENT.faqItems.map((item, index) => ({
							...item,
							question: t(translations, `pricing.faqItems.${index}.question`, item.question),
							answer: t(translations, `pricing.faqItems.${index}.answer`, item.answer),
						}))}
					/>
				</div>
			</section>

			{/* CTA STRIP */}
			<section className="cta-strip">
				<svg className="cta-s-mark" width="400" height="533" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d={S_PATH} fill="white" />
				</svg>
				<div className="cta-strip-inner">
					<h2>{t(translations, "pricing.ctaStrip.heading", "Ready to travel better?")}</h2>
					<p>
						{t(
							translations,
							"pricing.ctaStrip.subtitle",
							"Unlock the full Stippl experience: AI planning, PDF export, calendar sync and more."
						)}
					</p>
					<div className="cta-strip-btns">
						<a
							href="/register"
							className="btn-green"
							onClick={(e) => {
								e.preventDefault();
								gate.requestPlan();
							}}
						>
							{t(translations, "pricing.ctaStrip.cta", "Get Stippl Pro, from €4.99/mo")}
						</a>
						<a
							href="/register"
							className="btn-outline-white"
							onClick={(e) => {
								e.preventDefault();
								gate.requestPlan();
							}}
						>
							{t(translations, "pricing.ctaStrip.signupFree", "Sign up free")}
						</a>
					</div>
				</div>
			</section>

			<Footer />
			<DownloadAppModal isOpen={showDownloadModal} onClose={() => setShowDownloadModal(false)} location={match.path} />
			<PlanGateModal gate={gate} title="Create a free account to start planning" />
		</div>
	);
};

export default B2CPricingPage;
